import React, { useState } from 'react';
import { Text, Textarea, VStack } from '@chakra-ui/react';
import { Field } from '@/components/ui/field';
import ConfirmationDialog from '@/components/common/ConfirmationDialog';
import type { FeedbackRating } from '@/client';
import { FEEDBACK_RATING } from './useFeedback';

interface FeedbackDialogProps {
	open: boolean;
	rating: FeedbackRating;
	isPending: boolean;
	onOpenChange: (open: boolean) => void;
	onSubmit: (comment?: string) => void;
}

/**
 * Lightweight dialog that appears after clicking thumbs up/down,
 * matching the Claude/ChatGPT pattern of an optional follow-up comment
 * before the feedback is actually submitted.
 */
const ChatFeedbackDialog: React.FC<FeedbackDialogProps> = ({ open, rating, isPending, onOpenChange, onSubmit }) => {
	const [comment, setComment] = useState('');

	const handleSubmit = () => {
		onSubmit(comment.trim() || undefined);
		setComment('');
	};

	const handleCancel = () => {
		setComment('');
		onOpenChange(false);
	};

	const title = rating === FEEDBACK_RATING.THUMBS_UP ? 'What did you like?' : 'What went wrong?';

	return (
		<ConfirmationDialog
			isOpen={open}
			onOpenChange={onOpenChange}
			title={title}
			confirmText="Submit"
			cancelText="Cancel"
			onConfirm={handleSubmit}
			onCancel={handleCancel}
			isLoading={isPending}
			loadingText="Submitting..."
		>
			<VStack gap={3} align="stretch" pt={2}>
				<Text textStyle="body.sm" color="fg.muted">
					Optional — your feedback helps improve the assistant.
				</Text>
				<Field>
					<Textarea
						placeholder="Add a comment..."
						value={comment}
						onChange={(e) => setComment(e.target.value)}
						rows={3}
						resize="none"
					/>
				</Field>
			</VStack>
		</ConfirmationDialog>
	);
};

export default ChatFeedbackDialog;
